import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { useCallback, useEffect, useRef, useState } from "react";
import ReactSignatureCanvas from "react-signature-canvas";

import { trpc } from "@/common/utils/trpc";
import { Box, Button, Group, Image, Modal, Stack, Text } from "@mantine/core";

import {
	CARE_FORM_DIALOG,
	CARE_FORM_DIALOG_SIGNATURE_CLEAR_BUTTON,
} from "@/setup/frontend/test-ids";
import { useIsMobile } from "@/common/hooks/use-mobile";

dayjs.extend(timezone);
dayjs.extend(utc);

export const defaultSignatureInputDialogDescription = `By selecting "Confirm", you agree that your digital signature is legally binding and has the same validity as a handwritten signature.`;

export function SignatureInput({
	value,
	onChange,
	label,
	readOnly,
	width = 370,
	height = 150,
	required,
	dialogDescription = defaultSignatureInputDialogDescription,
	signerName,
	children,
	onClickOverride,
	...otherProps
}: {
	value?: string | null;
	onChange: (value: string | null) => void;
	label?: string;
	readOnly?: boolean;
	width?: number;
	height?: number;
	required?: boolean;
	dialogDescription?: string;
	signerName?: string;
	children?: React.ReactNode;
	onClickOverride?: () => void;
} & {
	[key: string]: any;
}) {
	const [opened, setOpened] = useState(false);
	const [isSubstantialDraw, setIsSubstantialDraw] = useState(false);
	const [showInsufficientNote, setShowInsufficientNote] = useState(false);
	const [unableToSignFontSizePx, setUnableToSignFontSizePx] = useState(10);
	const ref = useRef<ReactSignatureCanvas>(null);
	const unableToSignBoxRef = useRef<HTMLDivElement>(null);
	const user = trpc.users.currentUser.useQuery();

	const onSave = useCallback(() => {
		if (ref.current) {
			if (ref.current.isEmpty()) {
				onChange(null);
			} else {
				// Add metadata to the signature
				const dataUrl = ref.current.toDataURL();
				const metadata = {
					timestamp: Date.now(),
					timezone: dayjs.tz.guess(),
					dimensions: { width, height },
					userAgent: navigator.userAgent,
					ip: user.data?.meta.ip,
					latitude: user.data?.meta.latitude,
					longitude: user.data?.meta.longitude,
				};
				const metadataStr = btoa(JSON.stringify(metadata));
				const encodedDataUrl = `${dataUrl}#${metadataStr}`;
				onChange(encodedDataUrl);
			}
			setOpened(false);
		}
	}, [onChange, setOpened, width, height]);

	const onClear = useCallback(() => {
		ref.current?.clear();
		setIsSubstantialDraw(false);
		setShowInsufficientNote(false);
	}, []);

	// Check if signature is substantial when drawing ends
	const handleEnd = useCallback(() => {
		if (ref.current) {
			// Get the data URL and check its length
			const dataUrl = ref.current.toDataURL();

			// The empty canvas still produces a data URL with some length
			// A substantial signature will have a significantly longer data URL
			// Typical empty canvas data URL length is around 1500-2000 characters
			// Setting threshold to 4000 to ensure some actual drawing has occurred
			const isSubstantial = dataUrl.length > width * 10;
			setIsSubstantialDraw(isSubstantial);

			// Show note if signature isn't substantial
			setShowInsufficientNote(!isSubstantial && !ref.current.isEmpty());
		}
	}, [width]);

	useEffect(() => {
		if (opened && value) {
			// wait for the canvas to be mounted before loading the signature
			let timeElapsed = 0;
			const interval = setInterval(() => {
				timeElapsed += 100;
				if (timeElapsed > 3000) {
					clearInterval(interval);
					return;
				}
				const el = ref.current?.getCanvas();
				if (el && el.offsetParent !== null) {
					const [dataUrl] = value.split("#");

					ref.current?.fromDataURL(dataUrl, {
						height,
						width,
					});
					setIsSubstantialDraw(true);
					setShowInsufficientNote(false);
					clearInterval(interval);
				}
			}, 100);
			return () => clearInterval(interval);
		}
	}, [opened, value]);

	const isMobile = useIsMobile();

	// Check if value is a valid signature data URL
	const isValidSignature = value?.startsWith("data:image/");
	const isUnableToSignText = value && !isValidSignature;

	useEffect(() => {
		if (!isUnableToSignText || !unableToSignBoxRef.current) return;
		const el = unableToSignBoxRef.current;
		const id = requestAnimationFrame(() => {
			const { width, height } = el.getBoundingClientRect();
			const minDim = Math.min(width, height);
			setUnableToSignFontSizePx(Math.max(8, Math.min(14, minDim * 0.2)));
		});
		return () => cancelAnimationFrame(id);
	}, [isUnableToSignText]);

	return (
		<>
			{isValidSignature ? (
				<Image
					{...otherProps}
					src={value?.split("#")[0]} // Strip metadata for display
					onClick={readOnly ? undefined : () => setOpened(true)}
					style={{
						maxHeight: "100%",
						maxWidth: "100%",
						height: "100%",
						width: "100%",
						objectFit: "contain",
						cursor: readOnly ? "default" : "pointer",
					}}
				/>
			) : isUnableToSignText ? (
				<Box
					ref={unableToSignBoxRef}
					{...otherProps}
					style={{
						maxHeight: "100%",
						maxWidth: "100%",
						height: "100%",
						width: "100%",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						padding: "4px",
						backgroundColor: "var(--mantine-color-orange-1)",
						border: "1px solid var(--mantine-color-orange-3)",
						borderRadius: "4px",
						cursor: readOnly ? "default" : "pointer",
					}}
					onClick={
						readOnly
							? undefined
							: () => (onClickOverride ? onClickOverride() : setOpened(true))
					}
				>
					<Text
						style={{ fontSize: `${unableToSignFontSizePx}px` }}
						c="orange"
						ta="center"
						fw={500}
						lh={1.45}
					>
						{value}
					</Text>
				</Box>
			) : !readOnly ? (
				<Button
					{...otherProps}
					onClick={() => setOpened(true)}
					variant="light"
					style={(theme) => ({
						maxHeight: "100%",
						maxWidth: "100%",
						height: "100%",
						width: "100%",
						color: required ? theme.colors.red[5] : undefined,
						backgroundColor: required ? theme.colors.red[0] : undefined,
					})}
					mih={30}
				>
					{`${label || "Sign"} ${required ? "(required)" : ""}`}
				</Button>
			) : null}

			<Modal
				opened={opened}
				onClose={() => setOpened(false)}
				title={label || "Sign Here"}
				size={children || width > 370 ? "lg" : "md"}
				padding="md"
				data-testid={CARE_FORM_DIALOG}
				fullScreen={isMobile}
				styles={{
					header: {
						paddingTop: "var(--mantine-spacing-xs)",
					},
				}}
				removeScrollProps={{
					allowPinchZoom: true,
				}}
				centered
			>
				<Stack style={{ flex: 1 }}>
					<Stack style={{ flexGrow: 1 }}>
						<Text size="sm">{dialogDescription}</Text>

						{signerName && (
							<Text size="lg" fw={500}>
								Must be signed by {signerName}
							</Text>
						)}
						<Box
							style={{
								display: "flex",
								flexDirection: "column",
								justifyContent: "center",
								alignItems: "center",
							}}
							aria-label="Signature canvas"
						>
							<ReactSignatureCanvas
								ref={ref}
								canvasProps={{
									id: "react-signature-canvas",
									width,
									height,
									style: {
										background: "white",
										width,
										height,
										borderRadius: 5,
										border: "1px solid #ccc",
									},
								}}
								// Disabled to prevent race condition: orientation changes trigger clear() mid-stroke,
								// causing _strokeUpdate to crash accessing emptied _data array (SENTRY-7391401526)
								clearOnResize={false}
								onEnd={handleEnd}
							/>
							{showInsufficientNote && (
								<Text size="sm" c="orange" fw={500} mt={4}>
									Your signature is too small or simple. Please provide a more
									complete signature.
								</Text>
							)}
						</Box>
						{children}
					</Stack>

					<Group justify="flex-end" gap="xs" grow={isMobile}>
						<Button
							variant="outline"
							onClick={onClear}
							data-testid={CARE_FORM_DIALOG_SIGNATURE_CLEAR_BUTTON}
						>
							Clear
						</Button>
						<Button
							onClick={onSave}
							// allow confirming an empty signature to clear out the existing signature while still preventing unsubstantial signatures
							disabled={!isSubstantialDraw && !ref.current?.isEmpty()}
						>
							Confirm
						</Button>
					</Group>
				</Stack>
			</Modal>
		</>
	);
}
